import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ExportToolbar from "../components/ExportToolbar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Mail, Plus, Send, Search, Filter, Clock, CheckCircle, AlertCircle, ArrowRight,
  MessageCircle, Loader2, BarChart3, X, ChevronDown, Tag, Link, AlertTriangle, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CORR_TYPES = ["RFI","Notice","Instruction","Letter","Memo","Claim_Notice","Extension_of_Time","Variation_Order","Payment_Certificate","Site_Instruction","Non_Conformance","Meeting_Minutes","Submittal","Other"];
const PRIORITIES = ["critical","high","medium","low"];

const StatusBadge = ({ status }) => {
  const c = { open: "bg-blue-100 text-blue-700", ongoing: "bg-amber-100 text-amber-700", closed: "bg-emerald-100 text-emerald-700" };
  return <Badge className={`${c[status] || c.open} border-none text-xs`} data-testid={`status-${status}`}>{status}</Badge>;
};
const PriorityBadge = ({ level }) => {
  const c = { critical: "bg-red-100 text-red-700", high: "bg-orange-100 text-orange-700", medium: "bg-amber-100 text-amber-700", low: "bg-green-100 text-green-700" };
  return <Badge className={`${c[level] || c.medium} border-none text-xs`}>{level}</Badge>;
};

const CorrespondencesView = ({ contracts }) => {
  const [activeTab, setActiveTab] = useState("list");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [form, setForm] = useState({
    contract_id: "", subject: "", type: "Letter", priority: "medium",
    from_party: "", to_party: "", body: "", due_date: "",
    linked_change_order_id: "", linked_risk_id: "", tags: ""
  });

  useEffect(() => { fetchAll(); }, [filterStatus, filterType, searchTerm]);

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);
      if (filterType) params.append("type", filterType);
      if (searchTerm) params.append("search", searchTerm);
      const res = await axios.get(`${API}/correspondences?${params.toString()}`);
      setItems(res.data.correspondences || []);
      setStats(res.data.stats || {});
    } catch (e) { /* no items yet */ }
  };

  const createCorrespondence = async () => {
    if (!form.subject || !form.body || !form.from_party || !form.to_party) {
      toast.error("Fill in subject, body, from, and to fields"); return;
    }
    setLoading(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map(t => t.trim()) : [] };
      if (!payload.contract_id) delete payload.contract_id;
      if (!payload.linked_change_order_id) delete payload.linked_change_order_id;
      if (!payload.linked_risk_id) delete payload.linked_risk_id;
      if (!payload.due_date) delete payload.due_date;
      await axios.post(`${API}/correspondences`, payload);
      toast.success("Correspondence created");
      setShowCreate(false);
      setForm({ contract_id: "", subject: "", type: "Letter", priority: "medium", from_party: "", to_party: "", body: "", due_date: "", linked_change_order_id: "", linked_risk_id: "", tags: "" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setLoading(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/correspondences/${selected.id}/reply`, { body: replyText, from_party: "Me" });
      setSelected(res.data);
      setReplyText("");
      fetchAll();
      toast.success("Reply sent");
    } catch (e) { toast.error("Failed to send reply"); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status, notes = "") => {
    try {
      const res = await axios.put(`${API}/correspondences/${id}/status`, { status, resolution_notes: notes });
      setSelected(res.data);
      fetchAll();
      toast.success(`Status updated to ${status}`);
    } catch (e) { toast.error("Failed to update status"); }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/correspondences/analyze`);
      setAnalysisResult(res.data.analysis);
      setActiveTab("analysis");
      toast.success("Analysis complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="correspondences-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Correspondences</h1>
          <p className="text-zinc-500 mt-1">Track, manage and analyze all project communications</p>
        </div>
        <div className="flex gap-2">
          <ExportToolbar title="Correspondences" pdfFilename="correspondences" csvData={items.length ? items.map(c => ({ subject: c.subject, from: c.from_party, to: c.to_party, type: c.type, status: c.status, date: c.created_at })) : null} csvFilename="correspondences" />
          <Button onClick={runAnalysis} disabled={loading} variant="outline" className="border-2" data-testid="btn-analyze-corr">
            <BarChart3 size={16} className="mr-2" /> AI Analyze
          </Button>
          <Button onClick={() => { setShowCreate(true); setActiveTab("create"); }} className="bg-black text-white" data-testid="btn-new-corr">
            <Plus size={16} className="mr-2" /> New Correspondence
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border-2 border-black bg-white p-4 text-center brutal-shadow" data-testid="stat-total">
          <p className="text-2xl font-black">{stats.total || 0}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Total</p>
        </div>
        <div className="border-2 border-blue-300 bg-blue-50 p-4 text-center" data-testid="stat-open">
          <p className="text-2xl font-black text-blue-700">{stats.open || 0}</p>
          <p className="text-xs font-bold uppercase text-blue-500">Open</p>
        </div>
        <div className="border-2 border-amber-300 bg-amber-50 p-4 text-center" data-testid="stat-ongoing">
          <p className="text-2xl font-black text-amber-700">{stats.ongoing || 0}</p>
          <p className="text-xs font-bold uppercase text-amber-500">Ongoing</p>
        </div>
        <div className="border-2 border-emerald-300 bg-emerald-50 p-4 text-center" data-testid="stat-closed">
          <p className="text-2xl font-black text-emerald-700">{stats.closed || 0}</p>
          <p className="text-xs font-bold uppercase text-emerald-500">Closed</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 border-2 border-zinc-200">
          <TabsTrigger value="list" data-testid="tab-corr-list"><Mail size={14} className="mr-1" /> All ({items.length})</TabsTrigger>
          {selected && <TabsTrigger value="detail" data-testid="tab-corr-detail"><MessageCircle size={14} className="mr-1" /> Detail</TabsTrigger>}
          {showCreate && <TabsTrigger value="create" data-testid="tab-corr-create"><Plus size={14} className="mr-1" /> New</TabsTrigger>}
          {analysisResult && <TabsTrigger value="analysis" data-testid="tab-corr-analysis"><BarChart3 size={14} className="mr-1" /> Analysis</TabsTrigger>}
        </TabsList>

        <TabsContent value="list">
          <div className="border-2 border-black brutal-shadow bg-white">
            <div className="p-4 border-b border-zinc-200 flex gap-3 flex-wrap items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-3 text-zinc-400" />
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by subject, tracking ID..." className="w-full border-2 border-zinc-200 pl-9 pr-3 py-2 text-sm" data-testid="corr-search" />
              </div>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border-2 border-zinc-200 p-2 text-sm" data-testid="corr-filter-status">
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="ongoing">Ongoing</option>
                <option value="closed">Closed</option>
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} className="border-2 border-zinc-200 p-2 text-sm" data-testid="corr-filter-type">
                <option value="">All Types</option>
                {CORR_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <ScrollArea className="h-[500px]">
              {items.length === 0 ? (
                <div className="p-12 text-center text-zinc-400">
                  <Mail size={48} className="mx-auto mb-4 opacity-30" />
                  <p>No correspondences found. Create one to get started.</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {items.map((item, i) => (
                    <div key={i} className="p-4 hover:bg-zinc-50 cursor-pointer flex items-center gap-4"
                      onClick={() => { setSelected(item); setActiveTab("detail"); }} data-testid={`corr-item-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-[#0047AB] font-bold">{item.tracking_id}</span>
                          <StatusBadge status={item.status} />
                          <PriorityBadge level={item.priority} />
                          <Badge variant="outline" className="text-xs">{item.type?.replace(/_/g, " ")}</Badge>
                        </div>
                        <p className="font-medium text-sm truncate">{item.subject}</p>
                        <p className="text-xs text-zinc-500 mt-1">{item.from_party} → {item.to_party} | {item.replies?.length || 0} replies</p>
                      </div>
                      <div className="text-right text-xs text-zinc-400 flex-shrink-0">
                        <p>{new Date(item.created_at).toLocaleDateString()}</p>
                        {item.due_date && <p className="text-amber-600">Due: {new Date(item.due_date).toLocaleDateString()}</p>}
                      </div>
                      <ArrowRight size={16} className="text-zinc-300 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </TabsContent>

        {selected && (
          <TabsContent value="detail">
            <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm text-[#0047AB] font-bold">{selected.tracking_id}</span>
                    <StatusBadge status={selected.status} />
                    <PriorityBadge level={selected.priority} />
                  </div>
                  <h2 className="text-lg font-bold">{selected.subject}</h2>
                  <p className="text-sm text-zinc-500">{selected.type?.replace(/_/g, " ")} | {selected.from_party} → {selected.to_party}</p>
                </div>
                <div className="flex gap-2">
                  {selected.status === "open" && <Button size="sm" onClick={() => updateStatus(selected.id, "ongoing")} className="bg-amber-500 text-white" data-testid="btn-mark-ongoing">Mark Ongoing</Button>}
                  {selected.status !== "closed" && <Button size="sm" onClick={() => updateStatus(selected.id, "closed", "Resolved")} className="bg-emerald-600 text-white" data-testid="btn-close-corr">Close</Button>}
                  {selected.status === "closed" && <Button size="sm" onClick={() => updateStatus(selected.id, "open")} variant="outline" data-testid="btn-reopen-corr">Reopen</Button>}
                </div>
              </div>

              <div className="p-4 bg-zinc-50 border border-zinc-200 text-sm whitespace-pre-wrap">{selected.body}</div>

              {selected.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">{selected.tags.map((t, i) => <Badge key={i} variant="outline" className="text-xs"><Tag size={10} className="mr-1" />{t}</Badge>)}</div>
              )}

              <div>
                <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Replies ({selected.replies?.length || 0})</h3>
                <div className="space-y-3 mb-4">
                  {(selected.replies || []).map((r, i) => (
                    <div key={i} className="p-3 border-l-4 border-[#0047AB] bg-blue-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{r.from_party || r.created_by_name}</span>
                        <span className="text-xs text-zinc-400">{new Date(r.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm">{r.body}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={replyText} onChange={e => setReplyText(e.target.value)} onKeyPress={e => e.key === 'Enter' && sendReply()} placeholder="Type your reply..." className="flex-1 border-2 border-zinc-200 px-4 py-2 text-sm" data-testid="corr-reply-input" />
                  <Button onClick={sendReply} disabled={loading || !replyText.trim()} className="bg-[#0047AB] text-white" data-testid="btn-send-reply">
                    <Send size={16} />
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {showCreate && (
          <TabsContent value="create">
            <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-4">
              <h2 className="text-lg font-bold font-['Chivo']">New Correspondence</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Contract (Optional)</label>
                  <select value={form.contract_id} onChange={e => setForm(p => ({ ...p, contract_id: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="corr-contract-select">
                    <option value="">No contract linked</option>
                    {(contracts || []).map(c => <option key={c.id} value={c.id}>{c.project_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Type</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="corr-type-select">
                    {CORR_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="corr-priority-select">
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Subject</label>
                <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="Correspondence subject..." data-testid="corr-subject" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">From</label>
                  <input value={form.from_party} onChange={e => setForm(p => ({ ...p, from_party: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="Sender name/org" data-testid="corr-from" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">To</label>
                  <input value={form.to_party} onChange={e => setForm(p => ({ ...p, to_party: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="Recipient name/org" data-testid="corr-to" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Body</label>
                <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} className="w-full border-2 border-zinc-200 p-3 text-sm h-32" placeholder="Correspondence content..." data-testid="corr-body" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Due Date (Optional)</label>
                  <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="corr-due-date" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Tags (comma separated)</label>
                  <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="e.g., urgent, payment, design" data-testid="corr-tags" />
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={createCorrespondence} disabled={loading} className="bg-black text-white px-8" data-testid="btn-create-corr">
                  {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />} Create & Send
                </Button>
                <Button onClick={() => { setShowCreate(false); setActiveTab("list"); }} variant="outline">Cancel</Button>
              </div>
            </div>
          </TabsContent>
        )}

        {analysisResult && (
          <TabsContent value="analysis">
            <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-4">
              <h2 className="text-lg font-bold font-['Chivo']">Correspondence Analysis</h2>
              {analysisResult.summary && <div className="p-4 bg-blue-50 border border-blue-200"><p className="text-sm">{analysisResult.summary}</p></div>}
              {analysisResult.patterns?.map((p, i) => (
                <div key={i} className="p-3 border border-zinc-200 flex items-center gap-3">
                  <PriorityBadge level={p.risk_level} />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{p.pattern}</p>
                    <p className="text-xs text-zinc-500">{p.recommendation}</p>
                  </div>
                </div>
              ))}
              {analysisResult.escalations?.map((e, i) => (
                <div key={i} className="p-3 border-l-4 border-red-400 bg-red-50">
                  <p className="text-sm font-medium">{e.correspondence}: {e.reason}</p>
                </div>
              ))}
              {analysisResult.recommendations?.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm"><CheckCircle size={14} className="text-emerald-500 mt-0.5" /><span>{r}</span></div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default CorrespondencesView;
